import { Page, PageHeader } from '@/components/layout/page';
import { OfferCard } from '@/components/payment/offer-card';
import { DevNote } from '@/components/ui/dev-note';
import { Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { useSite } from '@/hooks/use-site';
import { describeOffer } from '@/lib/format';

/**
 * Page tarif.
 *
 * Elle ne fait **aucun appel** : l'offre est déjà dans la réponse de
 * `GET /site`, chargée au démarrage. C'est tout l'intérêt de cet endpoint —
 * il évite d'interroger `GET /offer` sur chaque page qui affiche un prix.
 *
 * Le robot de vérification de mise en production visite `/tarifs` et en fait
 * une capture d'écran : c'est l'une des pages attendues d'un site en
 * production.
 */
export default function PricingPage() {
    const site = useSite();

    return (
        <Page title="Tarif">
            <PageHeader
                eyebrow="Accès"
                title="Une offre unique, sans engagement caché"
                description={
                    site.offer
                        ? describeOffer(site.offer)
                        : "Ce site n'a pas encore configuré son offre."
                }
            />

            <Section>
                <Container className="grid items-start gap-12 lg:grid-cols-[1fr_1.1fr]">
                    <div className="space-y-8">
                        <div className="prose-doc">
                            <h2>Ce qui est inclus</h2>
                            <p>
                                L'accès ouvre la consultation des fiches complètes : identité et immatriculation,
                                capital social, activité, dirigeants, effectifs, établissements, juridiction compétente
                                et identifiant européen.
                            </p>

                            <h2>Comment se déroule le paiement</h2>
                            <p>
                                La carte est saisie sur notre page, dans un champ fourni par Stripe. Les données
                                bancaires vont directement du navigateur à Stripe : elles ne transitent ni par ce site,
                                ni par nos serveurs. L'authentification forte de votre banque, quand elle est
                                demandée, se déroule au même endroit.
                            </p>

                            <h2>Et si je change d'avis ?</h2>
                            <p>
                                Pour une offre par abonnement, la résiliation se fait en ligne, à tout moment et sans
                                justification — depuis votre compte, ou depuis la page de résiliation si vous n'avez
                                jamais défini de mot de passe. L'accès reste ouvert jusqu'à la fin de la période déjà
                                réglée.
                            </p>
                        </div>

                        <OfferTypesExplainer />
                    </div>

                    <div className="space-y-6 lg:sticky lg:top-28">
                        <OfferCard />

                        <DevNote title="Ce que fait cet écran" endpoints={['GET /site']}>
                            <p>
                                Aucun appel n'est émis par cette page : le nom de l'offre, son type et son tarif
                                proviennent de <code className="font-mono">site.offer</code>, déjà chargé au démarrage.
                            </p>
                            <p>
                                Un site n'a qu'<strong>une seule offre</strong>, active en permanence. Il n'y a donc pas
                                d'identifiant d'offre à transporter dans le tunnel :{' '}
                                <code className="font-mono">POST /payment</code> n'en prend pas.
                            </p>
                        </DevNote>
                    </div>
                </Container>
            </Section>
        </Page>
    );
}

const OFFER_TYPES = [
    {
        type: 'one_time',
        title: 'Paiement unique',
        body: "Un montant réglé une fois, qui ouvre l'accès pour une durée déterminée.",
        stripe: 'Un PaymentIntent du montant de l’offre',
        paymentId: 'pi_…',
    },
    {
        type: 'subscription',
        title: 'Abonnement',
        body: 'Un prélèvement récurrent, mensuel ou annuel, résiliable à tout moment.',
        stripe: 'Une souscription créée en default_incomplete ; le secret vient de sa première facture',
        paymentId: 'sub_…',
    },
    {
        type: 'trial_then_subscription',
        title: 'Essai payant puis abonnement',
        body: "Une première période à tarif réduit, puis l'abonnement plein tarif prend le relais.",
        stripe: "Un PaymentIntent du montant d’essai qui enregistre la carte, puis l’abonnement démarre avec une période d’essai couvrant les jours déjà payés",
        paymentId: 'pi_…',
    },
];

/**
 * Les trois types d'offre existants et leur mécanique Stripe.
 *
 * Point essentiel pour un intégrateur : **la requête et la réponse sont
 * identiques dans les trois cas**. Le backend choisit la mécanique adaptée et
 * renvoie toujours un `client_secret` et un `payment_id`. Il n'y a qu'un seul
 * chemin de code à écrire, et changer de type d'offre ne demande aucune
 * modification du front.
 */
function OfferTypesExplainer() {
    const site = useSite();

    return (
        <Card>
            <CardBody className="space-y-5">
                <div>
                    <h2 className="text-ink font-sans text-base font-semibold">Les trois types d'offre</h2>
                    <p className="text-ink-muted mt-1 text-sm">
                        Une même intégration les couvre tous. Le type de l'offre de ce site est{' '}
                        <code className="font-mono text-xs">{site.offer?.type ?? '—'}</code>.
                    </p>
                </div>

                <div className="space-y-4">
                    {OFFER_TYPES.map((entry) => (
                        <div
                            key={entry.type}
                            className={
                                site.offer?.type === entry.type
                                    ? 'border-brand-200 bg-brand-50/60 rounded-lg border p-4'
                                    : 'border-line rounded-lg border p-4'
                            }
                        >
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <p className="text-ink text-sm font-semibold">{entry.title}</p>
                                <code className="text-ink-muted font-mono text-xs">{entry.type}</code>
                            </div>

                            <p className="text-ink-soft mt-1 text-sm">{entry.body}</p>

                            <dl className="text-ink-muted mt-3 space-y-1 text-xs">
                                <div>
                                    <dt className="inline font-medium">Côté Stripe : </dt>
                                    <dd className="inline">{entry.stripe}</dd>
                                </div>
                                <div>
                                    <dt className="inline font-medium">payment_id : </dt>
                                    <dd className="inline font-mono">{entry.paymentId}</dd>
                                </div>
                            </dl>
                        </div>
                    ))}
                </div>

                <p className="text-ink-muted text-xs">
                    Le <code className="font-mono">payment_id</code> est <strong>opaque</strong> : sa forme dépend du
                    type d'offre, et il doit être renvoyé tel quel au claim, sans être interprété.
                </p>
            </CardBody>
        </Card>
    );
}
